import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import Product from '../../../model/product/product';
import Windowsticker from '../../../model/product/windowsticker/windowsticker';
import { ProductTypes } from '../../../model/product/product-type';
import Employee from 'src/app/model/product/employee';
import { Project } from 'src/app/model/project/project';
import PMSColor from 'src/app/model/company/pmscolor';
import { BehaviorSubject, Subject } from 'rxjs';
import BrowserHelper from 'src/app/helpers/browser-helper';
import Company from 'src/app/model/company/company';
import Board, { MaterialChoices } from 'src/app/model/product/board/board';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SnackService } from 'src/app/services/snack.service';
import ProductHelper from 'src/app/helpers/product-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import CustomWindowsticker from 'src/app/model/product/windowsticker/custom-windowsticker';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import BoardPart from 'src/app/model/product/board/board-part';
import ProjectHelper from 'src/app/helpers/project-helper';

@Component({
  selector: 'internal-project-form',
  templateUrl: './internal-project-form.component.html',
  styleUrls: ['./internal-project-form.component.scss'],
  providers: [FormatPhoneNumber],
})
export class InternalProjectFormComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  public instructions: ['Binnenkant', 'Buitenkant'];
  public hours: string[] = [];
  public minutes: string[] = [];
  public designerChoices: Employee[] = [];
  public printworkHandlerChoices: Employee[] = [];
  public finisherChoices: Employee[] = [];
  formGroup: UntypedFormGroup;
  designIsCollapsed = true;
  printworkIsCollapsed = true;
  finishIsCollapsed = true;
  safari: boolean;
  phoneNumberDisplay: string;

  renderDesign = new BehaviorSubject<boolean>(false);
  renderPrintwork = new BehaviorSubject<boolean>(false);
  renderFinish = new BehaviorSubject<boolean>(false);
  private designCollapsibleClicked = false;
  private printworkCollapsibleClicked = false;
  private finishCollapsibleClicked = false;

  private static getSignSizeDesignMinutes(boardPart: BoardPart): number {
    return boardPart.signSize == null
      ? null
      : boardPart.signSize.designerMinutes == null
      ? null
      : boardPart.signSize.designerMinutes;
  }

  @Input()
  public inputProject: Project;
  @Input()
  public inputProduct: Product;
  @Input()
  public involvedCompanies: Company[];
  @Input()
  public pmsColorAdded: Subject<PMSColor>;
  @Output()
  public updateProduct = new EventEmitter();

  public inputWindowsticker: Windowsticker;
  public inputBoard: Board;

  constructor(
    private formatPhoneNumber: FormatPhoneNumber,
    private employeeChoicesService: EmployeeChoicesService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit() {
    this.safari = BrowserHelper.isSafari();
    this.hours = ['0', '1', '2', '3', '4', '5', '6', '7', '8'];
    this.minutes = Array(60)
      .fill(1)
      .map((x, y) => String(x + y - 1).padStart(2, '0'));
    if (this.inputProduct.type === ProductTypes.WINDOWSTICKER.value) {
      this.inputWindowsticker = <Windowsticker>this.inputProduct;
    }
    if (this.isBoard()) {
      this.inputBoard = <Board>this.inputProduct;
      this.calculateDesignTimeBudget();
      const calculateDesignTimeBudgetObservable =
        this.createObservableUntilDestroy(
          this.projectDisplayObservableService.getProductPartDeletedSubscribable(),
          this.projectDisplayObservableService.getProductSizeChangedSubscribable()
        );
      this.addSubscription(calculateDesignTimeBudgetObservable, () =>
        this.calculateDesignTimeBudget()
      );
    }
    this.getEmployees();
    this.phoneNumberDisplay = this.selectedPhoneNumbersSeparated();
    this.addSubscription(
      this.projectDisplayObservableService.getProductPhoneNumberChangedSubscribable(),
      () => {
        this.phoneNumberDisplay = this.selectedPhoneNumbersSeparated();
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.renderDesign.complete();
    this.renderPrintwork.complete();
    this.renderFinish.complete();
    this.renderDesign = null;
    this.renderPrintwork = null;
    this.renderFinish = null;
  }

  isWindowSticker(): boolean {
    return this.inputProduct.type === ProductTypes.WINDOWSTICKER.value;
  }

  isCustomWindowSticker(): boolean {
    return this.inputProduct.type === ProductTypes.CUSTOMWINDOWSTICKER.value;
  }

  isBoard(): boolean {
    return this.inputProduct.type === ProductTypes.BOARD.value;
  }

  isOtherProduct(): boolean {
    return this.inputProduct.type === ProductTypes.OTHER_PRODUCT.value;
  }

  getEmployees() {
    this.getDesigners();
    this.getPrintworkHandlers();
    this.getFinishers();
  }

  getDesigners() {
    this.addSubscription(
      this.employeeChoicesService.getDesignerChoicesSubscribable(),
      (result) => {
        this.designerChoices = <Employee[]>result;
      }
    );
  }

  getPrintworkHandlers() {
    this.addSubscription(
      this.employeeChoicesService.getPrintworkHandlerChoicesSubscribable(),
      (result) => {
        this.printworkHandlerChoices = <Employee[]>result;
      }
    );
  }

  getFinishers() {
    this.addSubscription(
      this.employeeChoicesService.getFinisherChoicesSubscribable(),
      (result) => {
        this.finisherChoices = <Employee[]>result;
      }
    );
  }

  needsDesign(event) {
    this.inputProduct.needsDesign = event;
    if (event === 'yes') {
      delete this.inputProduct.needsDesignDescription;
    }
    if (event) {
      this.renderDesign.next(true);
      this.designIsCollapsed = false;
      this.designCollapsibleClicked = true;
    }
    this.updateProduct.emit();
  }

  needsDesignText(event) {
    this.inputProduct.needsDesignDescription = event;
    this.updateProduct.emit();
  }

  extraAttention(event) {
    this.inputProduct.extraAttentionFinish = event;
    if (event === 'no') {
      delete this.inputProduct.extraAttentionFinishDescription;
    }
    this.updateProduct.emit();
  }

  extraAttentionText(event) {
    this.inputProduct.extraAttentionFinishDescription = event;
    this.updateProduct.emit();
  }

  spellCheck(event) {
    this.inputProduct.spellCheck = event;
    this.updateProduct.emit();
  }

  designASAP(event) {
    this.inputProduct.designASAP = event;
    if (event) {
      delete this.inputProduct.designByDate;
    }
    this.updateProduct.emit();
  }

  designByDate(event) {
    this.updateProduct.emit();
  }

  designerChange(event) {
    this.projectDisplayObservableService.getDesignerPickedSubscribable().next();
    this.updateProduct.emit();
  }

  designerNotesChange(event) {
    this.inputProduct.designerNotes = event;
    this.updateProduct.emit();
  }

  designFinishedChange(event) {
    this.inputProduct.designFinished = event;
    this.updateProduct.emit();
  }

  titleCheckedDesignChange(event) {
    this.inputProduct.titleCheckedDesign = event;
    this.updateProduct.emit();
  }

  phoneNumberCheckedDesignChange(event) {
    this.inputProduct.phoneNumberCheckedDesign = event;
    this.updateProduct.emit();
  }

  titleCheckedPrintWorkChange(event) {
    this.inputProduct.titleCheckedPrintWork = event;
    this.updateProduct.emit();
  }

  setHours(event) {
    this.updateProduct.emit();
  }

  setMinutes(event) {
    this.updateProduct.emit();
  }

  emailTextChange(event) {
    this.inputProduct.emailText = event;
    this.updateProduct.emit();
  }

  customerDisapproves(event) {
    this.inputProduct.customerDisapproves = event;
    if (event) {
      this.inputProduct.customerApproves = false;
      this.inputProduct.designFinished = false;
    }
    this.updateProduct.emit();
  }

  customerDisapprovalFeedbackChange(event) {
    this.inputProduct.customerDisapprovalFeedback = event;
    this.updateProduct.emit();
  }

  customerDisapprovalActionChange(event) {
    this.inputProduct.customerDisapprovalAction = event;
    this.updateProduct.emit();
  }

  customerApproves(event) {
    this.inputProduct.customerApproves = event;
    if (event) {
      this.inputProduct.approvalDate = new Date();
    } else {
      this.inputProduct.approvalDate = null;
      this.inputProduct.designFinished = false;
    }
    this.updateProduct.emit();
  }

  customerApprovalFeedbackChange(event) {
    this.inputProduct.customerApprovalFeedback = event;
    this.updateProduct.emit();
  }

  customerApprovedByChange(event) {
    this.inputProduct.approvedBy = event;
    this.updateProduct.emit();
  }

  approvalDate(event) {
    this.updateProduct.emit();
  }

  printAndFinishPlannedChange(event) {
    this.inputProduct.printAndFinishPlanned = event;
    this.updateProduct.emit();
  }

  printByChange(event) {
    this.inputProduct.printByMutoh = event[0];
    this.inputProduct.printByAgfa = event[1];
    this.updateProduct.emit();
  }

  printWork(event) {
    this.inputProduct.needPrintwork = event;
    if (event === 'yes') {
      delete this.inputProduct.printworkExtraInfo;
    }
    if (event) {
      this.renderPrintwork.next(true);
      this.printworkIsCollapsed = false;
      this.printworkCollapsibleClicked = true;
    }
    this.updateProduct.emit();
  }

  noPrintworkExtraInfoChange(event) {
    this.inputProduct.noPrintworkExtraInfo = event;
    this.updateProduct.emit();
  }

  printWorkExtraInfo(event) {
    this.inputProduct.printworkExtraInfo = event;
    this.updateProduct.emit();
  }

  printworkASAP(event) {
    this.inputProduct.printworkASAP = event;
    if (event) {
      delete this.inputProduct.printworkByDate;
    }
    this.updateProduct.emit();
  }

  printworkByDate(event) {
    this.updateProduct.emit();
  }

  printworkHandlerChange(event) {
    this.updateProduct.emit();
  }

  printOperatorNotesChange(event) {
    this.inputProduct.printOperatorNotes = event;
    this.updateProduct.emit();
  }

  printworkFollowUpChange(event) {
    this.inputProduct.printworkFollowUp = event;
    this.updateProduct.emit();
  }

  printWorkReady(event) {
    this.inputProduct.printWorkReady = event;
    this.updateProduct.emit();
  }

  needFinish(event) {
    this.inputProduct.needFinish = event;
    if (event === 'no') {
      delete this.inputProduct.finishDescription;
    }
    if (event) {
      this.renderFinish.next(true);
      this.finishIsCollapsed = false;
      this.finishCollapsibleClicked = true;
    }
    this.updateProduct.emit();
  }

  finishDescription(event) {
    this.inputProduct.finishDescription = event;
    this.updateProduct.emit();
  }

  finishASAP(event) {
    this.inputProduct.finishASAP = event;
    if (event) {
      delete this.inputProduct.finishByDate;
    }
    this.updateProduct.emit();
  }

  finishByDate(event) {
    this.updateProduct.emit();
  }

  finisherChange(event) {
    this.updateProduct.emit();
  }

  finisherNotesChange(event) {
    this.inputProduct.finisherNotes = event;
    this.updateProduct.emit();
  }

  finishFollowUpChange(event) {
    this.inputProduct.finishFollowUp = event;
    this.updateProduct.emit();
  }

  numPandOk(event) {
    let windowSticker = <Windowsticker>this.inputProduct;
    windowSticker.numPandOk = event;
    this.updateProduct.emit();
  }

  setFinishHours(event) {
    this.updateProduct.emit();
  }

  setFinishMinutes(event) {
    this.updateProduct.emit();
  }

  inpressLogoChanged(event) {
    this.inputProduct.inpressLogo = event;
    this.updateProduct.emit();
  }

  finishReady(event) {
    this.inputProduct.finishReady = event;
    this.updateProduct.emit();
  }

  designSentToCustomerChange(event) {
    this.inputProduct.designSentToCustomer = event;
    this.updateProduct.emit();
  }

  tilingFormatsChange(event) {
    this.inputBoard.tilingFormats = event;
    this.updateProduct.emit();
  }

  toggleCollapseDesign() {
    this.designIsCollapsed = !this.designIsCollapsed;
    if (!this.designCollapsibleClicked) {
      this.designCollapsibleClicked = true;
      this.renderDesign.next(true);
    }
  }

  toggleCollapsePrintwork() {
    this.printworkIsCollapsed = !this.printworkIsCollapsed;
    if (!this.printworkCollapsibleClicked) {
      this.printworkCollapsibleClicked = true;
      this.renderPrintwork.next(true);
    }
  }

  toggleCollapseFinish() {
    this.finishIsCollapsed = !this.finishIsCollapsed;
    if (!this.finishCollapsibleClicked) {
      this.finishCollapsibleClicked = true;
      this.renderFinish.next(true);
    }
  }

  getProductNoun() {
    if (this.isWindowSticker() || this.isCustomWindowSticker()) {
      return 'raam';
    }
    if (this.isBoard()) {
      return 'bord';
    }
  }

  getDesignFormSection() {
    if (this.isWindowSticker() || this.isCustomWindowSticker()) {
      return 'windowDesign';
    }
    if (this.isBoard()) {
      return 'boardDesign';
    }
    return 'otherDesign';
  }

  compareEntities(o1, o2) {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      if (o1.id === o2.id) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  ordinalEnumGetter(o: any): string {
    return GeneralHelper.ordinalEnumGetter(o);
  }

  compareOrdinalEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }

  compareNumbers(n1, n2) {
    return n1 == n2;
  }

  calculateDesignTimeBudget(): void {
    if (
      (this.inputProduct.designerHourBudget == null ||
        this.inputProduct.designerHourBudget === 0) &&
      (this.inputProduct.designerMinuteBudget == null ||
        this.inputProduct.designerMinuteBudget === 0) &&
      CollectionHelper.isPopulated(this.inputBoard.boardParts)
    ) {
      const designerMinutes = CollectionHelper.findMax(
        this.inputBoard.boardParts,
        (bp) => InternalProjectFormComponent.getSignSizeDesignMinutes(bp)
      );
      if (designerMinutes === -Infinity || designerMinutes === 0) {
        return;
      }
      const [hours, minutes] =
        GeneralHelper.getMinutesInHourAndMinutesArray(designerMinutes);
      this.inputProduct.designerHourBudget = hours;
      this.inputProduct.designerMinuteBudget = minutes;
    }
  }

  designFinishedVerification(): boolean {
    if (this.inputProduct.inpressLogo == null) {
      this.snackService.showError(
        'Gelieve aan te geven of het Inpress logo mag toegevoegd worden'
      );
      return false;
    }
    if (
      ProjectHelper.displayImmoscoopOptionForProjectContacts(
        this.inputProject.projectContacts
      ) &&
      this.inputProduct.immoscoopLogo == null
    ) {
      this.snackService.showError(
        'Gelieve aan te geven of het Immoscoop logo mag toegevoegd worden'
      );
      return false;
    }
    if (
      ProjectHelper.displaySpottoOptionForProjectContacts(
        this.inputProject.projectContacts
      ) &&
      this.inputProduct.spottoLogo == null
    ) {
      this.snackService.showError(
        'Gelieve aan te geven of het Spotto logo mag toegevoegd worden'
      );
      return false;
    }
    if (!this.inputProduct.spellCheck) {
      this.snackService.showError(
        'Gelieve controle spelling op ja te zetten indien uitgevoerd'
      );
      return false;
    }
    if (!this.inputProduct.titleCheckedDesign) {
      this.snackService.showError(
        'Gelieve controle titel op ja te zetten indien uitgevoerd'
      );
      return false;
    }
    if (!this.inputProduct.phoneNumberCheckedDesign) {
      this.snackService.showError(
        'Gelieve controle telefoonnummer op ja te zetten indien uitgevoerd'
      );
      return false;
    }
    if (!this.inputProduct.designSentToCustomer) {
      this.snackService.showError('Ontwerp is nog niet verstuurd naar klant');
      return false;
    }
    if (!this.inputProduct.customerApproves) {
      this.snackService.showError('Ontwerp is nog niet goedgekeurd door klant');
      return false;
    }
    if (!(this.inputProduct.printByMutoh || this.inputProduct.printByAgfa)) {
      this.snackService.showError('Ontwerp is nog niet verstuurd naar printer');
      return false;
    }
    if (
      !CollectionHelper.isPopulated(this.inputProduct.productAssets) ||
      !this.inputProduct.productAssets.some(
        (pa) => !pa.archived && pa.formSection === 'internalDesign'
      )
    ) {
      this.snackService.showError('Gelieve minstens één design te uploaden');
      return false;
    }
    if (this.isWindowSticker() || this.isCustomWindowSticker()) {
      const windowSticker = <Windowsticker>this.inputProduct;
      if (windowSticker.foil == null) {
        this.snackService.showError(
          'Gelieve een keuze te maken voor materiaal'
        );
        return false;
      }
      if (windowSticker.laminate == null) {
        this.snackService.showError('Gelieve een keuze te maken voor laminaat');
        return false;
      }
      if (!this.checkWindowStickerHasSize(windowSticker)) {
        return false;
      }
      if (!ProductHelper.checkWindowStickerTilingSpecified(windowSticker)) {
        this.snackService.showError('Gelieve een keuze te maken bij tiling');
        return false;
      }
    }
    if (this.inputBoard != null) {
      if (StringHelper.isEmpty(this.inputBoard.tilingFormats)) {
        this.snackService.showError('Gelieve tiling formaten in te vullen');
        return false;
      }
      if (!this.checkBoard(<Board>this.inputBoard)) {
        return false;
      }
    }
    return (
      !ProductHelper.isOtherProduct(this.inputProduct) ||
      this.checkOtherProduct(<OtherProduct>this.inputProduct)
    );
  }

  private checkWindowStickerHasSize(windowSticker: Windowsticker): boolean {
    return ProductHelper.isCustomWindowSticker(windowSticker)
      ? this.checkCustomWindowStickerHasSize(<CustomWindowsticker>windowSticker)
      : this.checkStandardWindowStickerHasSize(windowSticker);
  }

  private checkStandardWindowStickerHasSize(
    windowSticker: Windowsticker
  ): boolean {
    if (!CollectionHelper.isPopulated(windowSticker.standardStickerParts)) {
      return true;
    }
    for (const standardStickerPart of windowSticker.standardStickerParts) {
      if (standardStickerPart.productSize == null) {
        this.snackService.showError(
          'Gelieve een formaat, hoogte en breedte te kiezen voor elke deelsticker'
        );
        return false;
      }
    }
    return true;
  }

  private checkCustomWindowStickerHasSize(
    customWindowSticker: CustomWindowsticker
  ): boolean {
    if (!CollectionHelper.isPopulated(customWindowSticker.stickerParts)) {
      return true;
    }
    for (const stickerPart of customWindowSticker.stickerParts) {
      if (stickerPart.windowHeight == null || stickerPart.windowWidth == null) {
        this.snackService.showError(
          'Gelieve een hoogte en breedte in te vullen voor het raam voor elke deelsticker'
        );
        return false;
      }
      if (
        stickerPart.stickerHeight == null ||
        stickerPart.stickerWidth == null
      ) {
        this.snackService.showError(
          'Gelieve een hoogte en breedte in te vullen voor de sticker voor elke deelsticker'
        );
        return false;
      }
    }
    return true;
  }

  private checkBoard(board: Board): boolean {
    if (!CollectionHelper.isPopulated(board.boardParts)) {
      return true;
    }
    for (const boardPart of board.boardParts) {
      if (boardPart.material == null) {
        this.snackService.showError(
          'Gelieve een materiaal te kiezen voor elk deelbord'
        );
        return false;
      }
      if (
        boardPart.material.ordinal === MaterialChoices.UNSPECIFIED.ordinal &&
        StringHelper.isEmpty(boardPart.otherMaterial)
      ) {
        this.snackService.showError(
          'Gelieve een beschrijving te geven van het ander materiaal voor elk deelbord indien van toepassing'
        );
        return false;
      }
      if (boardPart.height == null || boardPart.width == null) {
        this.snackService.showError(
          'Gelieve een hoogte en breedte in te vullen voor elk deelbord'
        );
        return false;
      }
    }
    return true;
  }

  private checkOtherProduct(otherProduct: OtherProduct): boolean {
    if (StringHelper.isEmpty(otherProduct.otherProductMaterial)) {
      this.snackService.showError('Gelieve materiaal in te vullen');
      return false;
    }
    if (otherProduct.height == null || otherProduct.width == null) {
      this.snackService.showError('Gelieve hoogte en breedte in te vullen');
      return false;
    }
    return true;
  }

  designFinishedVerificationCallback =
    this.designFinishedVerification.bind(this);

  printWorkReadyVerification(): boolean {
    if (!this.inputProduct.titleCheckedPrintWork) {
      this.snackService.showError(
        'Gelieve controle titel op ja te zetten indien uitgevoerd'
      );
      return false;
    }

    return true;
  }

  printWorkReadyVerificationCallback =
    this.printWorkReadyVerification.bind(this);

  selectedPhoneNumbersSeparated(): string {
    const toReturn = StringHelper.joinStringProperty(
      this.inputProduct.productPhoneNumbers,
      'phoneNumbers',
      ', ',
      this.formatPhoneNumber.transform
    );
    return StringHelper.isEmpty(toReturn)
      ? 'Telefoonnummer ontbreekt'
      : toReturn;
  }
}
